import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  type GateName,
  type GatesResponse,
  type LabEvent,
  type ModeName,
  type ModesResponse,
} from "./api.ts";

interface LabState {
  events: LabEvent[];
  connected: boolean;
  gates: GatesResponse | null;
  modes: ModesResponse | null;
  setGate: (gate: GateName, on: boolean) => Promise<void>;
  setMode: (mode: ModeName, on: boolean) => Promise<void>;
  refresh: () => Promise<void>;
  clearEvents: () => void;
}

const Ctx = createContext<LabState | null>(null);

const MAX_EVENTS = 400;

export function LabProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<LabEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [gates, setGates] = useState<GatesResponse | null>(null);
  const [modes, setModes] = useState<ModesResponse | null>(null);
  const buffer = useRef<LabEvent[]>([]);

  const refresh = useCallback(async () => {
    const [g, m] = await Promise.all([api.gates(), api.modes()]);
    setGates(g);
    setModes(m);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A single SSE connection for the whole app. Frames arrive faster than React
  // wants to re-render, so they are buffered and flushed on an interval.
  useEffect(() => {
    const source = new EventSource("/api/events");
    source.onopen = () => setConnected(true);
    source.onerror = () => setConnected(false);
    source.onmessage = (msg) => {
      const data = JSON.parse(msg.data) as LabEvent | { kind: "hello"; recent: LabEvent[] };
      if ("recent" in data) {
        setEvents(data.recent.slice(-MAX_EVENTS));
        return;
      }
      buffer.current.push(data);
    };

    const flush = setInterval(() => {
      if (!buffer.current.length) return;
      const incoming = buffer.current;
      buffer.current = [];
      setEvents((prev) => [...prev, ...incoming].slice(-MAX_EVENTS));
    }, 80);

    return () => {
      clearInterval(flush);
      source.close();
    };
  }, []);

  const setGate = useCallback(async (gate: GateName, on: boolean) => {
    setGates(await api.setGates({ [gate]: on }));
  }, []);

  const setMode = useCallback(async (mode: ModeName, on: boolean) => {
    setModes(await api.setModes({ [mode]: on }));
  }, []);

  const clearEvents = useCallback(() => setEvents([]), []);

  const value = useMemo(
    () => ({ events, connected, gates, modes, setGate, setMode, refresh, clearEvents }),
    [events, connected, gates, modes, setGate, setMode, refresh, clearEvents],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLab(): LabState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLab must be used inside <LabProvider>");
  return ctx;
}

/** Run an async demo, tracking pending state and surfacing the error. */
export function useRun<T>() {
  const [data, setData] = useState<T | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (demo: string, body: Record<string, unknown> = {}) => {
    setPending(true);
    setError(null);
    try {
      setData(await api.run<T>(demo, body));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }, []);

  return { data, pending, error, run, reset: () => setData(null) };
}
